import type { GraphData, MemoryCard, Reminder, TranscriptChunk, VideoDetails } from "./types";

const API_BASE_URL = "";
const CHAT_POLL_INTERVAL_MS = 2000;
const CHAT_POLL_ATTEMPTS = 60;

function apiUrl(path: string): string {
  return `${API_BASE_URL}${path}`;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(apiUrl(path), {
    ...init,
    cache: "no-store",
    headers: { "Content-Type": "application/json", ...init?.headers },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `HTTP_${response.status}`);
  }
  return response.json() as Promise<T>;
}

export const api = {
  listCards(params: Record<string, string | undefined> = {}) {
    const query = new URLSearchParams(Object.entries(params).filter((item): item is [string, string] => Boolean(item[1])));
    return request<{ cards: MemoryCard[] }>(`/api/cards?${query}`);
  },
  getCard(id: string) {
    return request<{ card: MemoryCard }>(`/api/cards/${encodeURIComponent(id)}`);
  },
  videoTranscript(id: string) {
    return request<{ details: VideoDetails | null; chunks: TranscriptChunk[] }>(`/api/cards/${encodeURIComponent(id)}/transcript`);
  },
  setVideoStatus(id: string, status: "to_watch" | "watched") {
    return request<{ ok: boolean }>(`/api/videos/${encodeURIComponent(id)}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
  },
  createCard(input: Record<string, unknown>) {
    return request<{ card: MemoryCard }>("/api/cards", { method: "POST", body: JSON.stringify(input) });
  },
  updateCard(id: string, input: Record<string, unknown>) {
    return request<{ card: MemoryCard }>(`/api/cards/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(input) });
  },
  deleteCard(id: string) {
    return request<{ ok: boolean }>(`/api/cards/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  restoreCard(id: string) {
    return request<{ ok: boolean }>(`/api/cards/${encodeURIComponent(id)}/restore`, { method: "POST" });
  },
  search(query: string) {
    return request<{ cards: MemoryCard[] }>(`/api/search?q=${encodeURIComponent(query)}`);
  },
  async chat(question: string) {
    const created = await request<{ jobId: string; status: "queued" }>("/api/chat", {
      method: "POST",
      body: JSON.stringify({ question }),
    });
    for (let attempt = 0; attempt < CHAT_POLL_ATTEMPTS; attempt += 1) {
      const result = await request<{
        status: "queued" | "processing" | "completed" | "failed";
        answer?: string;
        memoryCards?: MemoryCard[];
        webSources?: Array<{ title: string; url: string }>;
        error?: string;
      }>(`/api/chat/${encodeURIComponent(created.jobId)}`);
      if (result.status === "completed" && result.answer) {
        return {
          answer: result.answer,
          memoryCards: result.memoryCards ?? [],
          webSources: result.webSources ?? [],
        };
      }
      if (result.status === "failed") throw new Error(result.error ?? "CHAT_FAILED");
      await new Promise((resolve) => window.setTimeout(resolve, CHAT_POLL_INTERVAL_MS));
    }
    throw new Error("CHAT_TIMEOUT");
  },
  reminders() {
    return request<{ reminders: Reminder[] }>("/api/reminders");
  },
  graph() {
    return request<GraphData>("/api/graph");
  },
  settings() {
    return request<{
      settings: Record<string, unknown>;
      tavily: { usage: number; limit: number; percent: number } | null;
      supadata:
        | { configured: false }
        | {
            configured: true;
            usage: number;
            limit: number;
            percent: number;
            periodStart: string;
            periodEnd: string;
          };
      models: { primary: string | null; vision: string | null };
    }>("/api/settings");
  },
  saveSetting(key: string, value: unknown) {
    return request<{ ok: boolean }>(`/api/settings/${encodeURIComponent(key)}`, {
      method: "PUT",
      body: JSON.stringify({ value }),
    });
  },
  async exportData() {
    const response = await fetch(apiUrl("/api/export"));
    if (!response.ok) throw new Error(`HTTP_${response.status}`);
    return response.json() as Promise<Record<string, unknown>>;
  },
  fileUrl(id: string) {
    return apiUrl(`/api/files/${encodeURIComponent(id)}`);
  },
};
